import { API_ENDPOINT, COMPANY_LOGO_ENDPOINT } from '@/config/endpoint';
import { Box, Divider, Flex, Group, Stack, Text } from '@mantine/core';
import { PopularTagComponent, TagComponent } from './TechblogCard';
import {
  dateToString,
  emojiSource,
  isDark,
  useReplaceQueryParams,
} from '@/utils/utils';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '@/utils/localStorageControl';
import { useDocumentVisibility, useInterval } from '@mantine/hooks';
import { useEffect, useLayoutEffect, useState } from 'react';

import BookmarkButton from '@/components/Buttons/BookmarkButton';
import { CardProps } from './MobileCard';
import DMText from '@/components/DMText';
import Image from 'next/image';
import { NewTag } from '@/components/Badge';
import Skeleton from '@/components/Skeleton';
import { imageOpt } from '@/utils/images';
import { lastClickedTechblog } from '../techblog.state';
import themeVariables from '@/styles/themeVariables';
import { useAtomValue } from 'jotai';
import { useTechblogCardDesktopStyles } from '../techblog.style';

const DesktopCard = ({
  loading,
  handleCardClick,
  handleBookmarkClicked,
  techblog,
  bookmarkStatus,
}: CardProps) => {
  const { dark } = isDark();

  const documentState = useDocumentVisibility();
  const desktopStyles = useTechblogCardDesktopStyles({ dark });

  const { urlSearchParams, setQueryParams } = useReplaceQueryParams();
  const { id, company_id, title, published, summary, tag, is_popular } =
    techblog;

  const lastClicked = useAtomValue(lastClickedTechblog);

  const [checkBlog, setCheckBlog] = useState<boolean>(true);
  const [pageLeaveCounter, setPageLeaveCounter] = useState(0);

  const interval = useInterval(
    () => setPageLeaveCounter((value) => value + 1),
    1000
  );

  useLayoutEffect(() => {
    const checkId = getLocalStorageItem('techblog');
    setCheckBlog(checkId && checkId[id] === false ? false : true);
  }, []);

  useEffect(() => {
    if (documentState == 'hidden' && lastClicked.id === id) {
      interval.start();
    }

    if (documentState == 'visible') {
      const getClickBlog = getLocalStorageItem('techblog');
      if (
        pageLeaveCounter >= 5 &&
        lastClicked.id === id &&
        !Object.hasOwn(getClickBlog || {}, id)
      ) {
        setLocalStorageItem('techblog', { ...getClickBlog, [id]: false });
        setCheckBlog(false);
      }
      interval.stop();
      setPageLeaveCounter(0);
    }
  }, [documentState]);

  const handleGoodButtonClicked = (key) => async (e) => {
    e.stopPropagation();

    await API_ENDPOINT.techblog.evaluate(id, key === 'good' ? true : false);

    const checkId = getLocalStorageItem('techblog');
    checkId[id] = true;

    setLocalStorageItem('techblog', checkId);
    setCheckBlog(true);
  };

  const clickLogo = (id) => (e) => {
    e.stopPropagation();

    urlSearchParams.set('company_id', id);
    setQueryParams(urlSearchParams);
  };

  if (loading) {
    return (
      <Flex gap={30} className={desktopStyles.classes.skeletonContainer}>
        <Box w={90} h={90} sx={{ flexShrink: 0 }}>
          <Skeleton isImage width={90} height={90} />
        </Box>
        <Skeleton lines={4} height={120} />
      </Flex>
    );
  }

  return (
    <div
      className={desktopStyles.classes.outerContainer}
      onClick={handleCardClick}
    >
      <Image
        className={desktopStyles.classes.logoImage}
        width={64}
        height={64}
        src={imageOpt(`${COMPANY_LOGO_ENDPOINT}/${company_id}.png`, 100)}
        alt="logo"
        onClick={clickLogo(company_id)}
      />
      <Stack p={16} spacing={0}>
        <Box mb={8}>
          <Text span className={desktopStyles.classes.title}>
            {title}
          </Text>
          <NewTag published={published} />
          <PopularTagComponent isPopular={is_popular} />
        </Box>
        <DMText
          lineClamp={3}
          className={desktopStyles.classes.summary}
          dColor={themeVariables.darkMode.neutral}
        >
          {summary}
        </DMText>
        <Flex justify={'space-between'} align={'center'} h={30}>
          <div className={desktopStyles.classes.footerContainer}>
            <Box className={desktopStyles.classes.bookmarkButton}>
              <BookmarkButton
                size={24}
                active={bookmarkStatus}
                onClick={handleBookmarkClicked}
              />
            </Box>
            <Text className={desktopStyles.classes.footerDate}>
              {dateToString(published, 'YYYY.MM.DD')}
            </Text>
            {tag && tag.length > 0 && (
              <Divider
                className={desktopStyles.classes.footerDivider}
                orientation="vertical"
              />
            )}
            {tag && tag.length > 0 && <TagComponent tag={tag} />}
          </div>
          <Group
            spacing={6}
            sx={{
              display: checkBlog ? 'none' : 'flex',
            }}
          >
            <Flex
              className={desktopStyles.classes.goodEmoji}
              onClick={handleGoodButtonClicked('good')}
              align={'center'}
              gap={8}
            >
              <Image
                width={16}
                height={16}
                src={emojiSource('1f603', 'twitter')}
                alt="emoji"
              />
              <Text size={12} weight={600}>
                좋아요
              </Text>
            </Flex>
            <Flex
              className={desktopStyles.classes.badEmoji}
              onClick={handleGoodButtonClicked('bad')}
              align={'center'}
              gap={8}
            >
              <Image
                width={16}
                height={16}
                src={emojiSource('1f641', 'twitter')}
                alt="emoji"
              />
              <Text size={12} weight={600}>
                별로에요
              </Text>
            </Flex>
          </Group>
        </Flex>
      </Stack>
    </div>
  );
};

export default DesktopCard;
